import { VariantConfirmationMethod } from '../../methodVendorOptions';
import { SangerPrimerSet, VariantConfirmation, VariantConfirmationState } from '../../types';

/**
 * State machine for the Primer Management section. Single source of truth —
 * UI components only read from `availablePrimerActions`, never re-derive.
 *
 * Mutually exclusive actions, gated by the confirmation's lifecycle state:
 *
 * NEW / QUEUED (lab-queue states):
 *
 * | Action              | Condition                                                                           |
 * | ------------------- | ----------------------------------------------------------------------------------- |
 * | `replace`           | `primer_set != null`                                                                |
 * | `review-suggestion` | `primer_set == null && primer_set_suggestion != null && needs_new_primers === null` |
 * | `assign`            | `primer_set == null && needs_new_primers === true`                                  |
 *
 * `replace` is a single transaction: failing the current set and attaching
 * the new one happen behind one submit.
 *
 * REVIEWABLE (completion-time):
 *
 * | Action               | Condition                                  |
 * | -------------------- | ------------------------------------------ |
 * | `resolve-completion` | `primer_set?.state === 'new'`              |
 *
 * The `resolve-completion` action is the completion-flow gate: a `new` primer
 * set must be marked validated or failed before the confirmation can be
 * reconciled (see `isReconcileBlockedByPrimers`). All other primer mutations
 * are forbidden once the confirmation is past the lab queue.
 */

export type PrimerAction = 'assign' | 'replace' | 'review-suggestion' | 'resolve-completion';

export const PRIMER_SET_NEW_STATE = 'new';
export const PRIMER_SET_FAILED_STATE = 'failed';
export const PRIMER_SET_VALIDATED_STATE = 'validated';

export const SANGER_METHODS: ReadonlySet<string> = new Set<string>([
  VariantConfirmationMethod.Sanger,
  VariantConfirmationMethod.SangerLongRange,
]);

// Pre-completion states where assign / replace / review-suggestion are valid.
const PRIMER_LAB_QUEUE_STATES: ReadonlySet<VariantConfirmationState> = new Set([
  VariantConfirmationState.NEW,
  VariantConfirmationState.QUEUED,
]);

// PMS2/PMS2CL ambiguous pair, exempt from primers. Mirrors genes.py.
export const AMBIGUOUS_PMS2_GENE_NAME = 'PMS2/PMS2CL';

/**
 * Server-authoritative for a persisted confirmation: the backend
 * (`VariantConfirmation.requires_primers()`) owns the full rule — CNV, Sanger
 * method, and the PMS2/PMS2CL exemption — so the frontend just reads it.
 */
export const requiresPrimers = (confirmation: VariantConfirmation): boolean =>
  confirmation.requires_primers;

export const isPrimerSetFailed = (primerSet: Pick<SangerPrimerSet, 'state'> | null): boolean =>
  primerSet?.state === PRIMER_SET_FAILED_STATE;

/**
 * Client mirror of `mark_as_queued`'s precondition, for the Request dialog's
 * in-flight `method` (which changes before saving, so `requires_primers` can't
 * be read). Keep in sync with `requires_primers()` in models.py.
 */
export const isRequestBlockedByPrimers = (input: {
  gene: string;
  method: string;
  is_cnv: boolean;
  needs_new_primers: boolean | null;
}): boolean =>
  input.is_cnv &&
  input.gene !== AMBIGUOUS_PMS2_GENE_NAME &&
  SANGER_METHODS.has(input.method) &&
  input.needs_new_primers === null;

/**
 * Mirrors `mark_as_pending`'s primer precondition. `primer_set` is filtered to
 * non-failed sets server-side, so `null` covers "never assigned" and "all
 * previously-assigned were failed".
 */
export const isApproveBlockedByPrimers = (confirmation: VariantConfirmation): boolean =>
  requiresPrimers(confirmation) && confirmation.primer_set === null;

/**
 * A `new` primer set must be marked validated or failed before the
 * confirmation is completed. The backend's `mark_as_completed` does not
 * enforce this — the constraint is UI-only — so the gate must live both
 * here (Reconcile button disabled) and in `availablePrimerActions` (which
 * exposes `'resolve-completion'` to flip the primer state). The two co-vary
 * by construction.
 */
export const isReconcileBlockedByPrimers = (confirmation: VariantConfirmation): boolean =>
  requiresPrimers(confirmation) &&
  confirmation.state === VariantConfirmationState.REVIEWABLE &&
  confirmation.primer_set?.state === PRIMER_SET_NEW_STATE;

export const REQUEST_PRIMER_BLOCKER_MESSAGE =
  'Resolve the suggested primer set in the Primers section before requesting.';

export const APPROVE_PRIMER_BLOCKER_MESSAGE =
  'Assign primers in the Primers section before approving for sendout.';

export const RECONCILE_PRIMER_BLOCKER_MESSAGE =
  'Mark the primer set as validated or failed in the Primers section before completing.';

export const availablePrimerActions = (
  confirmation: VariantConfirmation
): ReadonlySet<PrimerAction> => {
  const actions = new Set<PrimerAction>();
  if (!requiresPrimers(confirmation)) return actions;

  // `resolve-completion` exists exactly to clear an
  // `isReconcileBlockedByPrimers` block, so they share one condition. At
  // review time `assign` / `replace` are off-limits because the lab has
  // already run the assay against whatever was attached.
  if (isReconcileBlockedByPrimers(confirmation)) {
    actions.add('resolve-completion');
    return actions;
  }

  if (!PRIMER_LAB_QUEUE_STATES.has(confirmation.state)) return actions;

  // Backend's `get_active_primer_set` excludes failed sets, so a non-null
  // `primer_set` always means "currently attached, replaceable".
  if (confirmation.primer_set !== null) {
    actions.add('replace');
    return actions;
  }

  // `needs_new_primers === null` means the user hasn't accepted or rejected
  // the suggestion yet (see `VariantConfirmation.needs_new_primers` in models.py).
  if (confirmation.primer_set_suggestion !== null && confirmation.needs_new_primers === null) {
    actions.add('review-suggestion');
    return actions;
  }
  if (confirmation.needs_new_primers === true) {
    actions.add('assign');
  }
  return actions;
};
